import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { loginAtom } from '../../../../lib/auth';
import { User } from '../../../../models/User';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Per-field render config: the i18n label key + how to read the value off the
// viewed client. Kept here (UI concern) rather than in the helper, which only
// owns data/logic — same split as GotoLinks' LINK_RENDER and Actions'
// ACTION_RENDER. The whole widget is rendered for any client type, but the
// non-email fields are only meaningful for User::User (matching the original
// gating), so each field's gate re-checks type here.
type FieldRender = {
  labelKey: string;
  gate: (object: User) => boolean;
  value: (object: User) => string;
};

const FIELD_RENDER: Record<string, FieldRender> = {
  email: {
    labelKey: 'coach.models.clients.email',
    gate: () => true,
    value: (o) => o.email || '',
  },
  phone_fixed: {
    labelKey: 'coach.models.clients.phone_fixed',
    gate: (o) => o?.type === 'User::User',
    value: (o) => o.phone_fixed || '',
  },
  phone_mobile: {
    labelKey: 'coach.models.clients.phone_mobile',
    gate: (o) => o?.type === 'User::User',
    value: (o) => o.phone_mobile || '',
  },
  address: {
    labelKey: 'coach.models.clients.address',
    gate: (o) => o?.type === 'User::User',
    // street + number + zip + city composed into one line, matching the
    // original layout (comma only when street is present).
    value: (o) => {
      const street = o.street || '';
      const num = o.number || '';
      const zip = o.zip || '';
      const city = o.city || '';
      const head = !!street ? `${street} ${num}`.trim() : '';
      const tail = `${zip} ${city}`.trim();
      return [head, tail].filter(Boolean).join(', ');
    },
  },
  country: {
    labelKey: 'coach.models.clients.country',
    gate: (o) => o?.type === 'User::User',
    value: (o) => o.country_name || '',
  },
};

type Props = {
  object: User;
}

export const CoachUserWidgetsDetailsContact: FunctionComponent<Props> = ({
  object
}) => {
  const [login] = useAtom(loginAtom);
  const navigate = useNavigate();
  const {t} = useTranslation(['translations']);

  // Contact settings are a coach preference, stored alongside the widget
  // layout on login.settings.userdetails_widgets.settings. `details_contact`
  // bundles { fields, hide_empty, edit } under one composite value.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.detailsContactValueKey] || {};
  const fields = cfg.fields || {};
  const hideEmpty = cfg.hide_empty !== false; // default on
  const editOn = cfg.edit !== false;          // default on

  // Fixed-order field items, each tagged with shown/hidden state.
  const ordered = HelpersClientWidgets.getOrderedDetailsContactFields(fields);

  // Render only shown fields whose gate passes; when `hide_empty` is on, also
  // skip fields whose value is blank.
  const rows = ordered
    .filter((item) => item.state === 'shown')
    .map((item) => {
      const render = FIELD_RENDER[item.key];
      if (!render || !render.gate(object)) return null;
      const value = render.value(object);
      if (hideEmpty && !value) return null;
      return { key: item.key, labelKey: render.labelKey, value };
    })
    .filter(Boolean) as { key: string; labelKey: string; value: string }[];

  // Edit affordance gate: only real users with basic consent. Toggled by the
  // widget's `edit` setting (default on).
  const canEdit = editOn
    && object?.type === 'User::User'
    && !!object.consent_status?.is_basic;

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* Edit CTA: small text button top-right, absolute in the title row
        (space-saving — costs no extra vertical space). Same navigate target
        as before. */}
    {canEdit && <Button
      label={t("coach.general.actions.edit_contact")}
      id="edit_contact"
      text
      size="small"
      startIcon={<HelpersClientWidgets.detailsContactEditIcon sx={{fontSize: '1.1rem'}} />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}`, {state: {detail: true}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.contact_information")}</Typography>
        <Divider />
      </Grid>
      {rows.map((row) => <Grid item xs={12} key={row.key}>
        <Typography variant="subtitle2" html>{t(row.labelKey)}</Typography>
        <Typography>{row.value}</Typography>
      </Grid>)}
    </Grid>
  </Paper>;
}
